import { useCallback, useMemo, useState } from 'react';

import { GlossarySaveResult, useGlossaryEntries } from '@/hooks/useGlossaryEntries';

import { GlossaryRow, PAGE_SIZE } from '../constants';

/** `useGlossary` の戻り値の型 */
export type UseGlossaryReturn = ReturnType<typeof useGlossary>;

/**
 * 用語集ページの状態とハンドラーを管理するカスタムフック。
 */
export const useGlossary = () => {
  /** 登録済み用語集エントリ一覧。redux-persist により永続化される。 */
  const { entries, setGlossaryEntries } = useGlossaryEntries();
  /** アップロードダイアログの開閉状態 */
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  /** エントリに含まれる言語コードの一覧（重複排除・登録順） */
  const languages = useMemo(
    () => [...new Set(entries.map((e) => e.language))],
    [entries],
  );
  /** タブで選択中の言語コード。null のときは先頭言語を表示する。 */
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);
  /** 実際に表示する言語コード */
  const currentLang = activeLanguage ?? languages[0] ?? null;

  /** 現在のページ番号（1始まり） */
  const [page, setPage] = useState(1);
  /** ページあたりの表示件数 */
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  /** 選択中言語でフィルタしたエントリ */
  const filtered = useMemo(
    () =>
      currentLang ? entries.filter((e) => e.language === currentLang) : [],
    [entries, currentLang],
  );
  /** 総ページ数 */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize],
  );
  /** 現在ページに表示するエントリ */
  const pageEntries = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  /**
   * 言語タブ切替。ページを1にリセットする。
   */
  const handleTabChange = useCallback((lang: string) => {
    setActiveLanguage(lang);
    setPage(1);
  }, []);

  /**
   * ページサイズ変更。ページを1にリセットする。
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  /**
   * アップロード完了時に呼ばれる。
   * エントリを更新しタブ・ページをリセットする。
   *
   * `setGlossaryEntries` はdispatch自体は同期的に行うため、
   * エントリ・タブ・ページのリセットはlocalStorageへの書き込み結果を待たずに行う
   * (`setGlossaryEntries` 呼び出し時点で最初のawaitに達するまでの処理は同期実行されるため、
   * 戻り値のPromiseを待たなくてもdispatchは完了している)。
   * 戻り値はlocalStorageへの書き込み結果({@link GlossarySaveResult})を表し、
   * 呼び出し元（アップロードダイアログ）はこれをawaitして閉じるかどうかを判断する(PRISM-3110)。
   */
  const handleRegistered = useCallback(
    (newEntries: GlossaryRow[]): Promise<GlossarySaveResult> => {
      const resultPromise = setGlossaryEntries(newEntries);
      setPage(1);
      setActiveLanguage(null);

      return resultPromise;
    },
    [setGlossaryEntries],
  );

  return {
    entries,
    isUploadOpen,
    setIsUploadOpen,
    languages,
    currentLang,
    page,
    setPage,
    pageSize,
    totalPages,
    filteredCount: filtered.length,
    pageEntries,
    handleTabChange,
    handlePageSizeChange,
    handleRegistered,
  };
};
